import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import {
  ConfirmationService,
  InPostPointSelect,
  TableDataPayload,
  TableLazyLoad,
  ToastService,
} from '@web/shared/ui';
import { CollectionRequestBuilder } from '@web/common';
import { executeOrNull, Guid, once } from '@web/utils';
import {
  dictionaryCountryFromResponse,
  phoneNumberWithPrefixFromResponse,
  phoneNumberWithPrefixToRequest,
} from '@web/shared/module/dictionary';
import { AdminSubscriptionCollectionItem } from '../models/admin-subscription-collection-item';
import { AdminSubscriptionCollectionFilter } from '../models/admin-subscription-collection-filter';
import { AdminSubscriptionApiService } from '../api/services/admin-subscription-api.service';
import { AdminSubscriptionDetails } from '../models/admin-subscription-details';
import { AdminPaymentMethod } from '../models/admin-payment-method';
import { AdminSubscriptionShippingAddress } from '../models/admin-subscription-shipping-address';
import { AdminSubscriptionBillingAddress } from '../models/admin-subscription-billing-address';
import { AdminSubscriptionSubscriber } from '../models/admin-subscription-subscriber';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { AdminSubscriptionItem } from '../models/admin-subscription-item';
import { AdminSubscriptionOrderCollectionFilter } from '../models/admin-subscription-order-collection-filter';
import { AdminSubscriptionOrderCollectionItem } from '../models/admin-subscription-order-collection-item';
import { AdminSubscriptionBillingAddressForm } from '../models/admin-subscription-billing-address-form';
import { AdminSubscriptionBillingAddressUpdateRequest } from '../api/requests/admin-subscription-billing-address-update.request';
import { AdminSubscriptionShippingAddressForm } from '../models/admin-subscription-shipping-address-form';
import { AdminSubscriptionShippingAddressUpdateRequest } from '../api/requests/admin-subscription-shipping-address-update.request';
import {
  refundSummaryFromResponse,
  shippingMethodDictionaryFromResponse,
} from '@web/shared/module/shop';
import { AdminSubscriptionsToProcessDetails } from '../models/admin-subscriptions-to-process-details';
import { AdminSubscriptionForm } from '../models/admin-subscription-form';
import { AdminSubscriptionUpdateRequest } from '../api/responses/admin-subscription-update.request';
import { AdminSubscriptionBulkPriceChangeForm } from '../models/admin-subscription-bulk-price-change-form';
import { AdminSubscriptionBulkPriceChangeRequest } from '../api/responses/admin-subscription-bulk-price-change.request';
import { AdminSubscriptionsBulkPriceChangeDetails } from '../models/admin-subscriptions-bulk-price-change-details';
import { AdminOwner } from '../models/admin-owner';

@Injectable({
  providedIn: 'root',
})
export class AdminSubscriptionDataSourceService {
  private readonly subscriptionApi = inject(AdminSubscriptionApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly toast = inject(ToastService);
  private readonly confirmation = inject(ConfirmationService);

  public fetchCollection(
    lazyLoad: TableLazyLoad<AdminSubscriptionCollectionFilter>,
  ): Observable<TableDataPayload<AdminSubscriptionCollectionItem>> {
    /* prettier-ignore */
    return this.subscriptionApi
      .fetchCollection(
        {
          request: this.collectionRequestBuilder
            .page(lazyLoad.page)
            .pageSize(lazyLoad.rows)
            .sorts((builder) =>
              builder.fromTableSort(lazyLoad.sort).buildAsArray(),
            )
            .filters((builder) =>
              builder
                .fromTableFilter(lazyLoad.filter)
                .appendDate((builder) => builder.rangeFor('createdAt').build())
                .appendDate((builder) => builder.rangeFor('lastPaymentDate').build())
                .appendDate((builder) => builder.rangeFor('nextPaymentDate').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('cardExpiryDate').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('subscriber').build())
                .appendText((builder) => builder.caseInsensitiveLikeFor('totalPrice').build())
                .appendText((builder) => builder.inFor('status').build())
                .appendText((builder) => builder.equalFor('isLocked').build())
                .buildAsArray(),
            )
            .build()
        },
      )
      .pipe(
        map(
          (response) => new TableDataPayload(
            response.items.map(
              (item) =>
                /* prettier-ignore */
                new AdminSubscriptionCollectionItem({
                  canBillingAddressBeUpdated: item.contextOperations.canBillingAddressBeUpdated,
                  canShippingAddressBeUpdated: item.contextOperations.canShippingAddressBeUpdated,
                  canBeViewed: item.contextOperations.canBeViewed,
                  canBeCancelled: item.contextOperations.canBeCancelled,
                  canBeProcessed: item.contextOperations.canBeProcessed,
                  canBeUpdated: item.contextOperations.canBeUpdated,
                  canBeResumed: item.contextOperations.canBeResumed,
                  canBeSuspended: item.contextOperations.canBeSuspended,
                  totalPrice: item.totalPrice,
                  id: item.id,
                  isLocked: item.isLocked,
                  number: item.number,
                  status: item.status,
                  subscriber: new AdminSubscriptionSubscriber({
                    dignity: item.subscriber.dignity,
                    id: item.subscriber.id,
                  }),
                  createdAt: item.createdAt,
                  cardExpiryDate: item.cardExpiryDate,
                  cardNumber: item.cardNumber,
                  lastPaymentDate: item.lastPaymentDate,
                  nextPaymentDate: item.nextPaymentDate,
                  secondPaymentDate: item.secondPaymentDate,
                  thirdPaymentDate: item.thirdPaymentDate,
                }),
            ),
            response.pageSize,
            response.page,
            response.total,
          )
        ),
      );
  }

  /* prettier-ignore */
  public fetchSubscriptionOrderCollection(options: {
    id: Guid;
    lazyLoad: TableLazyLoad<AdminSubscriptionOrderCollectionFilter>;
  }): Observable<TableDataPayload<AdminSubscriptionOrderCollectionItem>> {
    const { lazyLoad } = options;
    return this.subscriptionApi
      .fetchSubscriptionOrderCollection({
        id: options.id,
        request: this.collectionRequestBuilder
          .page(lazyLoad.page)
          .pageSize(lazyLoad.rows)
          .sorts((builder) =>
            builder.fromTableSort(lazyLoad.sort).buildAsArray(),
          )
          .filters((builder) =>
            builder
              .fromTableFilter(lazyLoad.filter)
              .appendDate((builder) => builder.rangeFor('createdAt').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('number').build())
              .appendText((builder) => builder.equalFor('totalItems').build())
              .appendText((builder) => builder.inFor('status').build())
              .appendText((builder) => builder.inFor('type').build())
              .appendText((builder) => builder.caseInsensitiveLikeFor('totalPrice').build())
              .buildAsArray(),
          )
          .build(),
      })
      .pipe(
        map(
          (response) =>
            new TableDataPayload(
              response.items.map(
                (item) =>
                  /* prettier-ignore */
                  new AdminSubscriptionOrderCollectionItem({
                    number: item.number,
                    status: item.status,
                    totalItems: item.totalItems,
                    id: item.id,
                    totalPrice: item.totalPrice,
                    refundSummary: executeOrNull(item.refundSummary, (summary) => refundSummaryFromResponse(summary)),
                    createdAt: item.createdAt,
                    type: item.type,
                  }),
              ),
              response.pageSize,
              response.page,
              response.total,
            ),
        )
      );
  }

  public confirmAndCancelById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz anulować wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .cancelById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została anulowana.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndSuspendById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wstrzymać wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .suspendById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została wstrzymana.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndResumeById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message('Czy na pewno chcesz wznowić wybraną subskrypcję?')
          .accept(() => {
            this.subscriptionApi
              .resumeById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została wznowiona.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public confirmAndProcessAvailable(): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz przetworzyć subskrypcje? Ta operacja spowoduje pobranie pieniędzy z kont klientów.',
          )
          .accept(() => {
            this.subscriptionApi
              .processAvailable()
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initInfo(
                        'Uruchomiono proces przetwarzania subskrypcji w tle.',
                      )
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public fetchSubscriptionsToProcessDetails(): Observable<AdminSubscriptionsToProcessDetails> {
    return this.subscriptionApi.fetchSubscriptionsToProcessDetails().pipe(
      map(
        (response) =>
          new AdminSubscriptionsToProcessDetails({
            itemsToProcess: response.itemsToProcess,
            isBulkProcessAvailable: response.isBulkProcessAvailable,
            nextScheduledProcessingDate: response.nextScheduledProcessingDate,
          }),
      ),
    );
  }

  public confirmAndProcessById(id: Guid): Observable<void> {
    return new Observable<void>((subscriber) => {
      this.confirmation.show((builder) =>
        builder
          .title('Potwierdzenie')
          .message(
            'Czy na pewno chcesz przetworzyć wybraną subskrypcję? Ta operacja spowoduje pobranie pieniędzy z konta klienta.',
          )
          .accept(() => {
            this.subscriptionApi
              .processById(id)
              .pipe(
                once(),
                tap(() => {
                  this.toast.show((builder) =>
                    builder
                      .initSuccess('Wybrana subskrypcja została przetworzona.')
                      .build(),
                  );
                }),
              )
              .subscribe(subscriber);
          })
          .build(),
      );
    });
  }

  public fetchFormById(id: Guid): Observable<AdminSubscriptionForm> {
    return this.subscriptionApi.fetchDetailsById({ id }).pipe(
      map(
        (response) =>
          new AdminSubscriptionForm({
            cardChargeDay: response.cardChargeDay,
          }),
      ),
    );
  }

  public updateSubscriptionByForm(params: {
    id: Guid;
    data: AdminSubscriptionForm;
  }): Observable<void> {
    const { id, data } = params;
    const request = new AdminSubscriptionUpdateRequest({
      cardChargeDay: data.cardChargeDay,
    });

    return this.subscriptionApi.updateSubscription({ id, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder.initSuccess('Subskrypcja została zaktualizowana.').build(),
        );
      }),
    );
  }

  public fetchDetailsById(id: Guid): Observable<AdminSubscriptionDetails> {
    return this.subscriptionApi.fetchDetailsById({ id }).pipe(
      map(
        (response) =>
          /* prettier-ignore */
          new AdminSubscriptionDetails({
            canBillingAddressBeUpdated: response.contextOperations.canBillingAddressBeUpdated,
            canShippingAddressBeUpdated:             response.contextOperations.canShippingAddressBeUpdated,
            cardChargeDay: response.cardChargeDay,
            id: response.id,
            isLocked: response.isLocked,
            number: response.number,
            status: response.status,
            totalPrice: response.totalPrice,
            itemsPrice: response.itemsPrice,
            shippingPrice: response.shippingPrice,
            lastPaymentDate: response.lastPaymentDate,
            nextPaymentDate: response.nextPaymentDate,
            cancelledDate: response.cancelledDate,
            suspendedDate: response.suspendedDate,
            resumedDate: response.resumedDate,
            cardChargeDayChangedDate: response.cardChargeDayChangedDate,
            owner: executeOrNull(
              response.owner,
              (owner) =>
                new AdminOwner({
                  id: owner.id,
                  name: owner.name,
                  email: owner.email,
                }),
            ),
            cancelReason: response.cancelReason,
            suspendReason: response.suspendReason,
            paymentMethod: new AdminPaymentMethod({
              name: response.paymentMethod.name,
              id: response.paymentMethod.name,
              code: response.paymentMethod.code,
            }),
            shippingMethod: shippingMethodDictionaryFromResponse(
              response.shippingMethod,
            ),
            shippingAddress: new AdminSubscriptionShippingAddress({
              city: response.shippingAddress.city,
              dignity: response.shippingAddress.dignity,
              email: response.shippingAddress.email,
              firstName: response.shippingAddress.firstName,
              lastName: response.shippingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(
                response.shippingAddress.phone,
              ),
              postCode: response.shippingAddress.postCode,
              streetLine1: response.shippingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.shippingAddress.country,
              ),
              streetLine2: response.shippingAddress.streetLine2,
              state: response.shippingAddress.state,
              companyName: response.shippingAddress.companyName,
              shippingPointName: response.shippingAddress.shippingPointName,
              shippingPoint: response.shippingAddress.shippingPoint,
            }),
            billingAddress: new AdminSubscriptionBillingAddress({
              city: response.billingAddress.city,
              dignity: response.billingAddress.dignity,
              email: response.billingAddress.email,
              firstName: response.billingAddress.firstName,
              lastName: response.billingAddress.lastName,
              phone: phoneNumberWithPrefixFromResponse(
                response.billingAddress.phone,
              ),
              postCode: response.billingAddress.postCode,
              streetLine1: response.billingAddress.streetLine1,
              country: dictionaryCountryFromResponse(
                response.billingAddress.country,
              ),
              streetLine2: response.billingAddress.streetLine2,
              state: response.billingAddress.state,
              companyName: response.billingAddress.companyName,
              companyNip: response.billingAddress.companyNip,
            }),
            items: response.items.map(
              (item) =>
                new AdminSubscriptionItem({
                  name: item.name,
                  price: item.price,
                  slug: item.slug,
                  featuredFile: executeOrNull(item.featuredFile, (file) =>
                    multimediaFromResponse(file),
                  ),
                  type: item.type,
                  quantity: item.quantity,
                  unitPrice: item.unitPrice,
                }),
            ),
          }),
      ),
    );
  }

  public fetchBillingAddressFormById(
    id: Guid,
  ): Observable<AdminSubscriptionBillingAddressForm> {
    return this.subscriptionApi.fetchBillingAddressDetails(id).pipe(
      map(
        (response) =>
          new AdminSubscriptionBillingAddressForm({
            city: response.city,
            companyName: response.companyName,
            companyNip: response.companyNip,
            countryId: response.country?.id,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            phone: phoneNumberWithPrefixFromResponse(response.phone),
            postCode: response.postCode,
            state: response.state,
            streetLine1: response.streetLine1,
            streetLine2: response.streetLine2,
          }),
      ),
    );
  }

  public fetchShippingAddressFormById(
    id: Guid,
  ): Observable<AdminSubscriptionShippingAddressForm> {
    return this.subscriptionApi.fetchShippingAddressDetails(id).pipe(
      map(
        (response) =>
          new AdminSubscriptionShippingAddressForm({
            city: response.city,
            companyName: response.companyName,
            countryId: response.country?.id,
            email: response.email,
            firstName: response.firstName,
            lastName: response.lastName,
            phone: phoneNumberWithPrefixFromResponse(response.phone),
            postCode: response.postCode,
            state: response.state,
            streetLine1: response.streetLine1,
            streetLine2: response.streetLine2,
            shippingMethodId: response.shippingMethod?.id,
            shippingPoint: executeOrNull(response.shippingPoint, (result) => {
              return new InPostPointSelect({
                description: result,
              });
            }),
            properties: {
              shippingPrice: response.shippingPrice,
              totalPrice: response.totalPrice,
              shippingMethod: shippingMethodDictionaryFromResponse(
                response.shippingMethod,
              ),
              shippingPoint: response.shippingPoint,
            },
          }),
      ),
    );
  }

  public updateBillingAddressByFormData(param: {
    id: Guid;
    data: AdminSubscriptionBillingAddressForm;
  }): Observable<void> {
    const { id, data } = param;
    const request = new AdminSubscriptionBillingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      companyNip: data.companyNip,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
    });

    return this.subscriptionApi.updateBillingAddress({ id, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess(
              'Adres rozliczeniowy dla subskrypcji został zaktualizowany.',
            )
            .build(),
        );
      }),
    );
  }

  public updateShippingAddressByFormData(param: {
    id: Guid;
    data: AdminSubscriptionShippingAddressForm;
  }): Observable<void> {
    const { id, data } = param;
    const request = new AdminSubscriptionShippingAddressUpdateRequest({
      city: data.city!,
      companyName: data.companyName,
      countryId: data.countryId!,
      email: data.email!,
      firstName: data.firstName!,
      lastName: data.lastName!,
      phone: phoneNumberWithPrefixToRequest(data.phone!),
      postCode: data.postCode!,
      state: data.state,
      streetLine1: data.streetLine1!,
      streetLine2: data.streetLine2,
      shippingMethod: data.shippingMethodId,
      shippingPoint: executeOrNull(data.shippingPoint, (point) => point.name),
    });

    return this.subscriptionApi.updateShippingAddress({ id, request }).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initSuccess('Adres dostawy dla subskrypcji został zaktualizowany.')
            .build(),
        );
      }),
    );
  }

  public bulkChangePriceByForm(
    data: AdminSubscriptionBulkPriceChangeForm,
  ): Observable<void> {
    const request = new AdminSubscriptionBulkPriceChangeRequest({
      price: String(data.price),
    });

    return this.subscriptionApi.bulkPriceChange(request).pipe(
      tap(() => {
        this.toast.show((builder) =>
          builder
            .initInfo('Uruchomiono proces zmiany cen subskrypcji w tle.')
            .build(),
        );
      }),
    );
  }

  public fetchBulkPriceChangeDetails(): Observable<AdminSubscriptionsBulkPriceChangeDetails> {
    return this.subscriptionApi.fetchBulkPriceChangeDetails().pipe(
      map(
        (response) =>
          new AdminSubscriptionsBulkPriceChangeDetails({
            isBulkPriceChangeAvailable: response.isBulkPriceChangeAvailable,
          }),
      ),
    );
  }
}
